/**
 * Node visual para geração automática de contrato no Flow Builder
 * Exibe configuração do contrato publicado, mapeamento de variáveis e signatários
 * 
 * RELEASE: 1.0.0 - 2026-03-22 - Novo node contract_generate
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { FileSignature, FileText, Users, Variable, ArrowRight, Send, Save } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { FlowNodeData, ContractGenerateConfig } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/**
 * Componente visual do node Gerar Contrato
 * Exibe contrato publicado selecionado, variáveis mapeadas e signatários
 */
export function ContractGenerateNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config as ContractGenerateConfig | undefined;

  // Verifica se node está configurado
  const isConfigured = !!config?.published_contract_id;

  // Conta variáveis mapeadas
  const mappedVarsCount = config?.variable_mapping
    ? Object.keys(config.variable_mapping).length
    : 0;

  // Conta signatários configurados
  const signersCount = config?.signers?.length || 0;

  return (
    <>
      {/* Toolbar flutuante para edição/exclusão */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={nodeData.onEdit as (() => void) | undefined}
        onDuplicate={(nodeData as any).onDuplicate as (() => void) | undefined}
        onDelete={nodeData.onDelete as (() => void) | undefined}
        onTestFromHere={(nodeData as any).onTestFromHere as (() => void) | undefined}
        isVisible={selected}
      />

      {/* Handle de entrada (topo) */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-teal-500 !border-2 !border-white"
      />

      {/* Corpo do node */}
      <div className={`
        min-w-[220px] max-w-[280px] bg-card rounded-lg border-2 shadow-lg
        ${selected ? 'border-teal-500 ring-2 ring-teal-500/30' : 'border-teal-400'}
        transition-all duration-200
      `}>
        {/* Header */}
        <div className="flex items-center gap-2 p-3 border-b border-border bg-teal-500/10 rounded-t-lg">
          <div className="p-1.5 rounded bg-teal-500">
            <FileSignature className="h-4 w-4 text-white" />
          </div>
          <span className="font-semibold text-sm">
            {config?.nickname || 'Gerar Contrato'}
          </span>
        </div>

        {/* Conteúdo */}
        <div className="p-3 space-y-2 text-xs">
          {isConfigured ? (
            <>
              {/* Contrato publicado selecionado */}
              <div className="flex items-center gap-1.5">
                <FileText className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Contrato:</span>
                <span className="font-medium truncate" title={config.published_contract_name}>
                  {config.published_contract_name || 'Selecionado'}
                </span>
              </div>

              {/* Variáveis mapeadas */}
              {mappedVarsCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <Variable className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Variáveis:</span>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                    {mappedVarsCount} mapeada{mappedVarsCount !== 1 ? 's' : ''}
                  </Badge>
                </div>
              )}

              {/* Signatários */}
              {signersCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <Users className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Signatários:</span>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                    {signersCount}
                  </Badge>
                </div>
              )}

              {/* Modo do deal */}
              <div className="flex items-center gap-1.5 pt-1 border-t border-border/50">
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Deal:</span>
                 <span className="font-medium">
                   {config.deal_selection === 'none' ? 'Sem negócio' : config.deal_selection === 'from_trigger' ? 'Do gatilho' : `{{${(config.deal_variable || '?').replace(/\{\{|\}\}/g, '')}}}`}
                 </span>
              </div>

              {/* Indicador de saída: contract_id */}
              <div className="flex items-center gap-1.5 text-teal-600 dark:text-teal-400 pt-1 border-t border-border/50">
                <span>→ Salva em:</span>
                <code className="bg-teal-100 dark:bg-teal-900/30 px-1 rounded text-[10px]">
                  {'{{contract_id}}'}
                </code>
              </div>

              {/* Auto-envio para assinatura */}
              {config.auto_send_signature && (
                <div className="flex items-center gap-1.5 pt-1 border-t border-border/50">
                  <Send className="h-3 w-3 text-teal-500" />
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300">
                    Auto-envio assinatura
                  </Badge>
                </div>
              )}

              {/* Variável de resultado */}
              {config.result_variable && (
                <div className="flex items-center gap-1.5 pt-1 border-t border-border/50">
                  <Save className="h-3 w-3 text-teal-500" />
                  <span className="text-muted-foreground">Resultado:</span>
                  <code className="bg-teal-100 dark:bg-teal-900/30 px-1 rounded text-[10px]">
                    {`{{${config.result_variable}}}`}
                  </code>
                </div>
              )}
            </>
          ) : (
            <p className="text-muted-foreground italic text-center py-2">
              Clique para configurar o contrato
            </p>
          )}
        </div>
      </div>

      {/* Handle de saída (base) */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-teal-500 !border-2 !border-white"
      />
    </>
  );
}
